import { Alert, AlertDescription, Button, FieldGroup } from "@monbits/ui";
import { CheckCheck, Copy } from "lucide-react";
import * as QRCode from "qrcode";
import * as React from "react";
import { toast } from "sonner";
import { OTPInput } from "src/components/otp-input/otp-input";

interface TwoFactorSetupProps {
	otpURL: string;
	secret: string;
	onVerify: (code: string) => Promise<boolean>;
	onCancel?: () => void;
	isSubmitting?: boolean;
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
	otpURL,
	secret,
	onVerify,
	onCancel,
	isSubmitting = false,
}) => {
	const [qrCodeURL, setQrCodeURL] = React.useState<string>("");
	const [qrCodeError, setQrCodeError] = React.useState<string>("");
	const [qrCodeLoading, setQrCodeLoading] = React.useState(true);
	const [code, setCode] = React.useState("");
	const [error, setError] = React.useState("");
	const [copied, setCopied] = React.useState(false);
	const [isVerifying, setIsVerifying] = React.useState(false);

	React.useEffect(() => {
		if (!otpURL) return;

		setQrCodeLoading(true);
		setQrCodeError("");

		QRCode.toDataURL(otpURL, {
			width: 256,
			margin: 2,
			errorCorrectionLevel: "M",
			type: "image/png",
		})
			.then((url) => {
				setQrCodeURL(url);
				setQrCodeLoading(false);
			})
			.catch(() => {
				setQrCodeError("Couldn't generate the QR code.");
				setQrCodeLoading(false);
				toast.error("Couldn't generate the QR code.");
			});
	}, [otpURL]);

	const handleVerify = React.useCallback(async () => {
		// Validate code before submitting
		if (!code || code.length !== 6 || isVerifying) {
			if (code.length > 0 && code.length !== 6) {
				setError("Enter the full 6-digit code.");
			}
			return;
		}

		setIsVerifying(true);
		try {
			const success = await onVerify(code);
			if (!success) {
				setError("That code didn't work. Try the latest one from your app.");
				setCode("");
			}
		} finally {
			setIsVerifying(false);
		}
	}, [code, onVerify, isVerifying]);

	const handleCodeChange = (value: string) => {
		setCode(value);
		if (error) {
			setError("");
		}
	};

	const handleCopySecret = () => {
		navigator.clipboard.writeText(secret);
		setCopied(true);
		toast.success("Secret copied to clipboard");
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div className="space-y-6">
			<Alert>
				<AlertDescription>
					Scan with an authenticator app — Google Authenticator, Authy, or
					1Password all work.
				</AlertDescription>
			</Alert>

			<div className="flex flex-col items-center">
				{qrCodeLoading ? (
					<div className="w-64 h-64 flex items-center justify-center bg-muted rounded-lg">
						<span className="text-sm text-muted-foreground">
							Generating QR code…
						</span>
					</div>
				) : qrCodeError ? (
					<div className="w-64 h-64 flex items-center justify-center bg-destructive/10 border border-destructive rounded-lg">
						<span className="text-sm text-destructive text-center px-4">
							{qrCodeError}
						</span>
					</div>
				) : (
					<img
						src={qrCodeURL}
						alt="QR Code"
						className="h-auto max-w-full rounded-lg border-2 border-border"
					/>
				)}
			</div>

			<div>
				<p className="text-sm font-medium mb-2">Or enter this key manually</p>
				<div className="flex items-center gap-2">
					<code className="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
						{secret}
					</code>
					<Button
						type="button"
						variant="outline"
						size="icon"
						aria-label={copied ? "Secret copied" : "Copy 2FA secret"}
						onClick={handleCopySecret}
					>
						{copied ? (
							<CheckCheck className="h-4 w-4" />
						) : (
							<Copy className="h-4 w-4" />
						)}
					</Button>
				</div>
			</div>

			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleVerify();
				}}
			>
				<FieldGroup>
					<OTPInput
						label="Verification code"
						description="Enter the 6-digit code from your authenticator app."
						value={code}
						onChange={handleCodeChange}
						onComplete={handleVerify}
						error={error}
						disabled={isSubmitting || isVerifying}
						required
					/>

					<div className="flex gap-2">
						<Button
							type="submit"
							disabled={isSubmitting || isVerifying || code.length !== 6}
							className="flex-1"
						>
							{isSubmitting || isVerifying ? "Verifying…" : "Enable 2FA"}
						</Button>

						{onCancel && (
							<Button
								type="button"
								variant="outline"
								onClick={onCancel}
								disabled={isSubmitting}
							>
								Cancel
							</Button>
						)}
					</div>
				</FieldGroup>
			</form>
		</div>
	);
};
